import _ from 'lodash-es';
import NoteComment from '~/scripts/services/model/note-comment';
import NoteContext from '~/scripts/services/model/note-context';
import { COMPOSED_OF } from '~/scripts/constants/common-kids';
import Repre from '~/scripts/services/model/repre';
import { Confirm } from '~/scripts/services/modal/modal';

/* globals Knitiv */

export default class NoteContextController {
	/** @type {import('@knitiv/api-client-javascript').KNode[]} */
	#linktoToDelete = [];

	/** @type {import('@knitiv/api-client-javascript').KNode[]} */
	#participantsToDelete = [];

	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {Knitiv.RootScope} $rootScope
	 * @param {import('angular').IScope} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {any} NodeService
	 * @param {any} User
	 */
	constructor(
		$injector,
		$rootScope,
		$scope,
		$timeout,
		$translate,
		NodeService,
		User,
	) {
		this.$injector = $injector;
		this.$rootScope = $rootScope;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.NodeService = NodeService;
		this.User = User;

		/** @type {import('../comments.controller').default | undefined} */
		this.commentsCtrl = undefined;

		/** @type {NoteContext} */
		this.ctx = new NoteContext();

		/** @type {boolean} */
		this.edit = false;

		/** @type {NoteComment | undefined} */
		this.newComment = undefined;

		/** @type {import('angular').IFormController | undefined} */
		this.noteContextForm = undefined;

		/** @type {Function | undefined} */
		this.onDelete = undefined;

		/** @type {boolean} */
		this.opened = true;

		/** @type {NoteContext} */
		this.tmpContext = new NoteContext();
	}

	cancel() {
		this.edit = false;
		this.#linktoToDelete.length = 0;
		this.#participantsToDelete.length = 0;
	}

	/**
	 * @param {MouseEvent} event
	 */
	async delete(event) {
		event.stopImmediatePropagation();
		event.preventDefault();
		const modal = new Confirm(this.$injector);
		const answer = await modal.open({
			title:  this.$translate.instant('notes.confirm-delete-context-title'),
			body:   `<p>${this.$translate.instant('notes.confirm-delete-context-message')}</p>`,
			cancel: {
				label: this.$translate.instant('common.cancel'),
				type:  'default',
			},
			accept: {
				label: this.$translate.instant('common.delete'),
				type:  'danger',
			},
		});

		if (answer) {
			const result = await this.NodeService.delete(this.ctx?.kid);
			if (result && typeof this.onDelete === 'function') {
				this.onDelete();
			}
		}
	}

	/**
	 * @param {any} linkto
	 */
	deleteLinkto(linkto) {
		this.#linktoToDelete.push(linkto.kid);
	}

	/**
	 * @param {any} participant
	 */
	deleteParticipant(participant) {
		this.#participantsToDelete.push(participant.kid);
	}

	/**
	 * @returns {string}
	 */
	getParticipants() {
		return this.$injector.get('$sce').trustAsHtml(this.ctx.getParticipants());
	}

	reset() {
		this.newComment = new NoteComment();
		this.newComment.edit();
		this.newComment.author = this.$rootScope.context?.profil.kid;
	}

	/**
	 * @param {?NoteComment} comment
	 */
	submit(comment = null) {
		this.$timeout(async () => {
			if (this.ctx) {
				if (comment?.kid) {
					// update comment
					await this.update(comment);
				}
				else if (comment && this.ctx.kid) {
					// add note to existing context
					const nodeNote = this.NodeService.create(comment.isa);
					nodeNote.addRepre(new Repre({
						type:  'K_NODE;HTML',
						value: comment.content,
					}));
					nodeNote.addRelation(
						COMPOSED_OF,
						this.NodeService.create(
							this.ctx.isa,
							this.ctx.kid,
						),
					);
					nodeNote.addRelation(
						COMPOSED_OF,
						this.NodeService.create(
							'K_NODE;KNOTETYPE',
							comment.type,
						),
					);
					if (comment.parent?.kid) {
						nodeNote.addRelation(
							COMPOSED_OF,
							this.NodeService.create(
								comment.parent.isa,
								comment.parent.kid,
							),
							'K_NODE;PARENT',
						);
					}
					const result = await this.NodeService.updateMultiple([nodeNote]);
					this.$timeout(() => {
						if (result && comment) {
							comment.kid = result._NR_replace[nodeNote.kid];
							if (comment.parent) {
								comment.update();
							}
							else {
								this.ctx?.addNote(new NoteComment({
									...comment,
								}));
							}
							this.reset();
						}
					});
				}
				else {
					const nodeNoteContext = this.NodeService.create('K_NODE;KNOTECTX');

					// create context
					nodeNoteContext.addRelationDate(this.ctx.date.created);

					// type
					nodeNoteContext.addRelation(COMPOSED_OF, this.NodeService.create(
						'K_NODE;KNOTECTXTYPE',
						this.ctx.type,
					));

					// owner
					nodeNoteContext.addRelation(
						COMPOSED_OF,
						this.NodeService.create(
							'K_NODE;PERSON',
							this.ctx.owner,
						),
						'K_NODE;OWNER',
					);

					// participants
					for (const participant of this.ctx.participants) {
						nodeNoteContext.addRelation(
							COMPOSED_OF,
							this.NodeService.create(
								'K_NODE;PERSON',
								participant.kid,
							),
							'K_NODE;PARTICIPANT',
							'K_NODE;PERSON',
						);
					}

					// affectedto K_NODE;ANYTHING / K_NODE;AFFECTED_TO
					for (const affectedTo of this.ctx.affectedTo) {
						nodeNoteContext.addRelation(
							COMPOSED_OF,
							this.NodeService.create(
								affectedTo.class_kid,
								affectedTo.kid,
							),
							'K_NODE;AFFECTED_TO',
							'K_NODE;ANYTHING',
						);
					}

					// linkto K_NODE;ANYTHING / K_NODE;LINKTO
					for (const linkto of this.ctx.linkto) {
						nodeNoteContext.addRelation(
							COMPOSED_OF,
							this.NodeService.create(
								'K_NODE;ANYTHING',
								linkto.kid,
							),
							'K_NODE;LINKTO',
						);
					}

					// note (newComment)
					const nodeNote = this.NodeService.create('K_NODE;KNOTE');
					// nodeNote.addRelationDate(note.date.created);
					nodeNote.addRepre(new Repre({
						type:  'K_NODE;HTML',
						value: this.newComment?.content,
					}));
					nodeNote.addRelation(
						COMPOSED_OF,
						this.NodeService.create(
							'K_NODE;KNOTECTX',
							nodeNoteContext.kid,
						),
					);
					nodeNote.addRelation(
						COMPOSED_OF,
						this.NodeService.create(
							'K_NODE;KNOTETYPE',
							this.newComment?.type,
						),
					);
					const result = await this.NodeService.updateMultiple([
						nodeNoteContext,
						nodeNote,
					]);

					if (result) {
						this.#linktoToDelete.length = 0;
						this.#participantsToDelete.length = 0;
						// @ts-ignore
						this.$timeout(() => {
							if (this.ctx && this.commentsCtrl && this.newComment) {
								this.ctx.kid = result._NR_replace[nodeNoteContext.kid];
								this.newComment.kid = result._NR_replace[nodeNote.kid];
								this.newComment.update();
								const newContext = new NoteContext({ ...this.ctx });
								newContext.addNote(this.newComment);
								this.opened = false;
								this.commentsCtrl.contexts.push(newContext);
								this.commentsCtrl.tmpContext.affectedTo.length = 0;
								this.commentsCtrl.tmpContext.linkto.length = 0;
								this.commentsCtrl.tmpContext.participants.length = 0;
								this.commentsCtrl.reset();
								this.reset();
							}
						});
					}
				}
			}
		});
	}

	/**
	 * @param {MouseEvent} event
	 */
	toggleEdit(event) {
		event.stopImmediatePropagation();
		event.preventDefault();
		this.edit = !this.edit;
		if (this.edit) {
			this.opened = true;
			this.tmpContext = new NoteContext(_.cloneDeep(this.ctx));
		}
	}

	/**
	 * Update a comment
	 * @param {NoteComment} comment
	 */
	async update(comment) {
		const nodeNote = this.NodeService.create('K_NODE;NOTE', comment.kid);
		nodeNote.addRepre(new Repre({
			type:  'K_NODE;HTML',
			value: comment.content,
		}));
		nodeNote.addRelation(
			COMPOSED_OF,
			this.NodeService.create(
				'K_NODE;KNOTETYPE',
				comment.type,
			),
		);
		try {
			const result = await this.NodeService.updateMultiple([nodeNote]);
			this.$timeout(() => {
				if (result) {
					comment.update();
				}
				else {
					comment.cancel();
				}
			});
		}
		catch (error) {
			console.error(error);
			comment.cancel();
		}
	}

	async updateContext() {
		try {
			const nodeNoteContext = this.NodeService.create(this.tmpContext.isa, this.tmpContext.kid);

			// type
			nodeNoteContext.addRelation(COMPOSED_OF, this.NodeService.create(
				'K_NODE;KNOTECTXTYPE',
				this.tmpContext.type,
			));

			// linkto K_NODE;ANYTHING / K_NODE;LINKTO
			for (const linkto of this.tmpContext.linkto) {
				nodeNoteContext.addRelation(
					COMPOSED_OF,
					this.NodeService.create(
						'K_NODE;ANYTHING',
						linkto.kid,
					),
					'K_NODE;LINKTO',
					'K_NODE;ANYTHING',
				);
			}

			for (const linkto of this.#linktoToDelete) {
				nodeNoteContext.delRelation({
					class_kid:    'K_NODE;ANYTHING',
					linker_kid:   'K_NODE;LINKTO',
					relation_kid: COMPOSED_OF,
					objnum2:      linkto,
				});
			}

			// participants
			for (const participant of this.tmpContext.participants) {
				nodeNoteContext.addRelation(
					COMPOSED_OF,
					this.NodeService.create(
						participant.class_kid,
						participant.kid,
					),
					'K_NODE;PARTICIPANT',
					'K_NODE;PERSON',
				);
			}

			for (const participant of this.#participantsToDelete) {
				nodeNoteContext.delRelation({
					class_kid:    'K_NODE;PERSON',
					linker_kid:   'K_NODE;PARTICIPANT',
					relation_kid: COMPOSED_OF,
					objnum2:      participant,
				});
			}

			const result = await this.NodeService.updateMultiple([nodeNoteContext]);
			this.$timeout(() => {
				if (result) {
					this.ctx = this.tmpContext;
					this.edit = false;
				}
			});
		}
		catch (error) {
			console.error(error);
		}
	}

	$onInit() {
		// this.$scope.$watch('$ctrl.notesCtrl.options', () => {
		// 	this.options = {
		// 		...this.defaultOptions,
		// 		...this.options,
		// 	};
		// 	if (this.ctx) {
		// 		this.ctx.type = this.options.defaultType;
		// 	}
		// }, true);

		if (!this.ctx?.kid) {
			this.edit = true;
			this.tmpContext.type = this.commentsCtrl?.options.defaultType;
		}

		this.$scope.$watch('$ctrl.commentsCtrl.contexts', () => {
			if (this.commentsCtrl?.contexts && this.commentsCtrl.contexts.length > 0 && !this.ctx?.kid) {
				this.opened = false;
			}
		});

		this.reset();
	}
}
